import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Title,
  Text,
  Button,
  Stack,
  Group,
  Box,
  Card,
  NumberInput,
  Select,
  rem,
  Badge,
  Textarea,
  Kbd
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import {
  IconCoin,
  IconShoppingCart,
  IconCheck,
  IconRefresh,
  IconCalendar
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { collection, query, where, onSnapshot, doc, setDoc, getDocs } from 'firebase/firestore';
import firestore from '../../utils/firestore';
import calendar from '../../utils/lunar';
import festival from '../../utils/festival';
import format_with_substring from '../../utils/number';

const DailyEdit = () => {
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [formData, setFormData] = useState({
    income: 0,
    specialIncome: 0,
    specialIncomeFrom: '',
    vendor: '',
    expenses: 0,
    memo: ''
  });
  const [existingRecord, setExistingRecord] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lunarInfo, setLunarInfo] = useState(null);
  const [festivalHistory, setFestivalHistory] = useState(null);
  const [showFestivalHistory, setShowFestivalHistory] = useState(false);

  // 獲取當天資料
  useEffect(() => {
    const year = selectedDate.year();
    const month = selectedDate.month();
    const day = selectedDate.date();

    const q = query(
      collection(firestore, 'accounting'),
      where('year', '==', year),
      where('month', '==', month),
      where('day', '==', day)
    );

    const unsubscribe = onSnapshot(q, accounting => {
      if (accounting.docs.length > 0) {
        const record = accounting.docs[0];
        const data = { id: record.id, ...record.data() };
        setExistingRecord(data);
        setFormData({
          income: data.income || 0,
          specialIncome: data.specialIncome || 0,
          specialIncomeFrom: data.specialIncomeFrom || '',
          vendor: data.vendor || '',
          expenses: data.expenses || 0,
          memo: data.memo || ''
        });
      } else {
        setExistingRecord(null);
        setFormData({
          income: 0,
          specialIncome: 0,
          specialIncomeFrom: '',
          vendor: '',
          expenses: 0,
          memo: ''
        });
      }
    });

    return () => unsubscribe();
  }, [selectedDate]);

  // 獲取農曆資訊
  useEffect(() => {
    const year = selectedDate.year();
    const month = selectedDate.month() + 1; // dayjs month is 0-indexed
    const day = selectedDate.date();

    const todayLunar = calendar.solar2lunar(year, month, day);
    const week = selectedDate.day();
    const weekNames = ['日', '一', '二', '三', '四', '五', '六'];

    let lunarStr = `${todayLunar.lMonth}/${todayLunar.lDay}`;
    let festivalName = '';

    if (festival[lunarStr]) {
      festivalName = festival[lunarStr];
    } else if (
      todayLunar.lDay !== 1 &&
      todayLunar.lDay !== 15 &&
      todayLunar.lDay !== 2 &&
      todayLunar.lDay !== 16
    ) {
      lunarStr = '';
    }

    setLunarInfo({
      lunar: lunarStr,
      festival: festivalName,
      weekName: weekNames[week]
    });

    if (festivalName) {
      loadFestivalHistory(festivalName, todayLunar.lMonth, todayLunar.lDay);
    } else {
      setFestivalHistory(null);
    }
  }, [selectedDate]);

  const loadFestivalHistory = async (festivalName, lunarMonth, lunarDay) => {
    try {
      const q = query(
        collection(firestore, 'accounting'),
        where('lunar', '==', festivalName)
      );
      const snapshot = await getDocs(q);

      const historyData = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        const totalIncome = (data.income || 0) + (data.specialIncome || 0);
        historyData.push({
          year: data.year,
          month: data.month,
          day: data.day,
          totalIncome,
          income: data.income || 0,
          specialIncome: data.specialIncome || 0,
          expenses: data.expenses || 0
        });
      });

      if (historyData.length > 0) {
        const sorted = historyData.sort((a, b) => b.year - a.year);
        setFestivalHistory(sorted);
      } else {
        setFestivalHistory(null);
      }
    } catch (error) {
      console.error('載入節日歷史失敗:', error);
    }
  };

  // 儲存記錄
  const handleSave = useCallback(async () => {
    if (formData.income === 0 && formData.specialIncome === 0 && formData.expenses === 0) {
      notifications.show({
        title: '無效資料',
        message: '請至少輸入一項收入或支出',
        color: 'orange'
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const saveData = {
        year: selectedDate.year(),
        month: selectedDate.month(),
        day: selectedDate.date(),
        income: formData.income || 0,
        specialIncome: formData.specialIncome || 0,
        specialIncomeFrom: formData.specialIncomeFrom || '',
        vendor: formData.vendor || '',
        expenses: formData.expenses || 0,
        memo: formData.memo || '',
        lunar: lunarInfo?.festival || lunarInfo?.lunar || ''
      };

      let docRef;
      if (existingRecord?.id) {
        docRef = doc(firestore, 'accounting', existingRecord.id);
      } else {
        docRef = doc(collection(firestore, 'accounting'));
      }

      await setDoc(docRef, saveData, { merge: true });
      notifications.show({
        title: '儲存成功',
        message: '記帳資料已儲存',
        color: 'green'
      });
    } catch (error) {
      notifications.show({
        title: '儲存失敗',
        message: error.message,
        color: 'red'
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, selectedDate, existingRecord, lunarInfo]);

  // 重置表單
  const handleReset = () => {
    setFormData({
      income: 0,
      specialIncome: 0,
      specialIncomeFrom: '',
      vendor: '',
      expenses: 0,
      memo: ''
    });
  };

  // 設定為今天
  const handleSetToday = () => {
    setSelectedDate(dayjs());
  };

  // 鍵盤快捷鍵
  const handleKeyDown = useCallback(
    event => {
      // Ctrl/Cmd + S = 儲存
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault();
        handleSave();
      }
      // Ctrl/Cmd + R = 重置
      if ((event.ctrlKey || event.metaKey) && event.key === 'r') {
        event.preventDefault();
        handleReset();
      }
      // Ctrl/Cmd + T = 今天
      if ((event.ctrlKey || event.metaKey) && event.key === 't') {
        event.preventDefault();
        handleSetToday();
      }
    },
    [handleSave]
  );

  // 綁定鍵盤事件
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const expenseCategories = [
    '小神堂',
    '源吉',
    '祥雲',
    '揚明',
    '伯明',
    '吉興',
    '祐恆',
    '香',
    '包-花袋',
    '雜支',
    '營業稅',
    '打火機'
  ];

  const specialIncomeFromOptions = [
    '親家',
    '阿賢',
    '亞田份',
    '金紙包',
    '姊',
    '邱太',
    '秀敏',
    '同安金紙',
    '鳳凰金紙',
    '春日金紙',
    '大業金紙',
    '南平金紙',
    '聖賢宮',
    '聖母宮'
  ];

  const netAmount =
    (formData.income || 0) + (formData.specialIncome || 0) - (formData.expenses || 0);

  return (
    <Container size="xl" p={0}>
      <Stack gap={{ base: 'sm', sm: 'xl' }}>
        {/* 標題與日期選擇 */}
        <Paper shadow="md" radius="lg" p={{ base: 'md', sm: 'xl' }} bg="blue.0">
          <Stack align="center" gap="md">
            <Title order={1} size="xl" c="blue.7">
              當天收支編輯
            </Title>
            <Stack gap="sm" align="center">
              <DateInput
                label="選擇日期"
                placeholder="選擇要編輯的日期"
                value={selectedDate.toDate()}
                onChange={date => {
                  if (date) {
                    setSelectedDate(dayjs(date));
                  }
                }}
                size="md"
                leftSection={<IconCalendar size={20} />}
                styles={{
                  label: { fontSize: rem(16), fontWeight: 600 },
                  input: { fontSize: rem(14), height: rem(42) }
                }}
                w={{ base: '100%', sm: 'auto' }}
              />
              <Button
                color="blue"
                variant="light"
                size="md"
                onClick={handleSetToday}
                style={{
                  fontSize: rem(14),
                  height: rem(42)
                }}
                w={{ base: '100%', sm: 'auto' }}
              >
                今天
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* 簡化的日期資訊卡片 */}
        <Paper shadow="md" radius="lg" p="lg" withBorder>
          <Group justify="center" align="center" gap="xl">
            <Box ta="center">
              <Text size="sm" c="dimmed" fw={500} mb={4}>
                日期
              </Text>
              <Title order={1} size={rem(42)} c="blue.8" fw={700} lh={1} mb="xs">
                {selectedDate.format('DD')}
              </Title>
            </Box>
            
            <Box ta="center">
              <Text size="md" fw={600} c="dark.7">
                {selectedDate.format('YYYY年MM月')}
              </Text>
              <Text size="sm" c="dimmed" mt={2}>
                星期{lunarInfo?.weekName}
              </Text>
              
              {(lunarInfo?.festival || lunarInfo?.lunar) && (
                <Box mt="xs">
                  {lunarInfo?.festival ? (
                    <Badge color="orange" size="sm" variant="light">
                      {lunarInfo.festival}
                    </Badge>
                  ) : (
                    <Badge color="gray" size="xs" variant="light">
                      農曆 {lunarInfo.lunar}
                    </Badge>
                  )}
                </Box>
              )}
            </Box>
          </Group>
        </Paper>

        {festivalHistory && festivalHistory.length > 0 && (
          <Paper shadow="md" radius="lg" p={{ base: 'md', sm: 'xl' }} withBorder>
            <Stack gap="md">
              <Group justify="space-between" align="center">
                <Group gap="xs">
                  <IconCalendar size={20} color="#fa5252" />
                  <Title order={3} size="md" c="dark.7">
                    {lunarInfo?.festival} - 歷年收入比較
                  </Title>
                  <Badge size="sm" variant="light" color="orange">
                    {festivalHistory.length} 筆
                  </Badge>
                </Group>
                <Button
                  variant="subtle"
                  size="sm"
                  onClick={() => setShowFestivalHistory(!showFestivalHistory)}
                >
                  {showFestivalHistory ? '收合' : '展開'}
                </Button>
              </Group>

              {showFestivalHistory && (
                <Stack gap="sm">
                  {festivalHistory.map((record, index) => {
                    const isHighest = festivalHistory[0].totalIncome === record.totalIncome;
                    const isLowest = festivalHistory[festivalHistory.length - 1].totalIncome === record.totalIncome;
                    const isSameYear = record.year === selectedDate.year();

                    return (
                      <Card
                        key={index}
                        withBorder
                        p="sm"
                        style={{
                          backgroundColor: isSameYear ? '#e7f5ff' : isHighest ? '#f0f9f0' : isLowest ? '#fff0f0' : 'white',
                          borderLeft: isSameYear
                            ? `${rem(4)} solid #228be6`
                            : isHighest
                            ? `${rem(4)} solid #51cf66`
                            : isLowest
                            ? `${rem(4)} solid #fa5252`
                            : 'none'
                        }}
                      >
                        <Group justify="space-between" align="center">
                          <Stack gap={0}>
                            <Group gap="xs" align="center">
                              <Text size="md" fw={600} c={isSameYear ? 'blue.7' : 'dark.7'} span>
                                {record.year}年{record.month + 1}月{record.day}日
                              </Text>
                              {isSameYear && (
                                <Badge size="xs" color="blue">
                                  今年
                                </Badge>
                              )}
                              {isHighest && !isSameYear && (
                                <Badge size="xs" color="green">
                                  最高
                                </Badge>
                              )}
                              {isLowest && !isSameYear && (
                                <Badge size="xs" color="red">
                                  最低
                                </Badge>
                              )}
                            </Group>
                            <Text size="xs" c="dimmed" span>
                              一般收入: ${format_with_substring(record.income)} • 特別收入: ${format_with_substring(
                                record.specialIncome
                              )} • 支出: ${format_with_substring(record.expenses)}
                            </Text>
                          </Stack>
                          <Text
                            size="lg"
                            fw={700}
                            c={isSameYear ? 'blue.6' : 'green.6'}
                          >
                            ${format_with_substring(record.totalIncome)}
                          </Text>
                        </Group>
                      </Card>
                    );
                  })}
                </Stack>
              )}
            </Stack>
          </Paper>
        )}

        {/* 編輯表單 */}
        <Paper shadow="md" radius="lg" p={{ base: 'md', sm: 'xl' }}>
          <Stack gap="md">
            <Title order={3} size="md" c="dark.7" mb="sm">
              收支資料
            </Title>

            {/* 收入區域 */}
            <Card withBorder p="md" style={{ borderLeft: `${rem(4)} solid #51cf66` }}>
              <Stack gap="md">
                <Group gap="xs" mb="xs">
                  <IconCoin size={20} color="#51cf66" />
                  <Text size="sm" fw={600} c="green.7">
                    收入項目
                  </Text>
                </Group>

                <NumberInput
                  label="一般收入"
                  placeholder="0"
                  value={formData.income || 0}
                  onChange={value => setFormData({ ...formData, income: value || 0 })}
                  min={0}
                  size="lg"
                  styles={{
                    label: { fontSize: rem(14), fontWeight: 600 },
                    input: { fontSize: rem(16), height: rem(45) }
                  }}
                />

                <Select
                  label="特別項目來源"
                  placeholder="選擇特別項目來源"
                  data={specialIncomeFromOptions}
                  value={formData.specialIncomeFrom}
                  onChange={value => setFormData({ ...formData, specialIncomeFrom: value || '' })}
                  searchable
                  clearable
                  size="lg"
                  styles={{
                    label: { fontSize: rem(14), fontWeight: 600 },
                    input: { fontSize: rem(16), height: rem(45) }
                  }}
                />

                <NumberInput
                  label="特別收入"
                  placeholder="0"
                  value={formData.specialIncome || 0}
                  onChange={value => setFormData({ ...formData, specialIncome: value || 0 })}
                  min={0}
                  size="lg"
                  styles={{
                    label: { fontSize: rem(14), fontWeight: 600 },
                    input: { fontSize: rem(16), height: rem(45) }
                  }}
                />
              </Stack>
            </Card>

            {/* 支出區域 */}
            <Card withBorder p="md" style={{ borderLeft: `${rem(4)} solid #fa5252` }}>
              <Stack gap="md">
                <Group gap="xs" mb="xs">
                  <IconShoppingCart size={20} color="#fa5252" />
                  <Text size="sm" fw={600} c="red.7">
                    支出項目
                  </Text>
                </Group>

                <Select
                  label="收款廠商"
                  placeholder="選擇廠商"
                  data={expenseCategories}
                  value={formData.vendor}
                  onChange={value => setFormData({ ...formData, vendor: value || '' })}
                  clearable
                  size="lg"
                  styles={{
                    label: { fontSize: rem(14), fontWeight: 600 },
                    input: { fontSize: rem(16), height: rem(45) }
                  }}
                />

                <NumberInput
                  label="支出金額"
                  placeholder="0"
                  value={formData.expenses || 0}
                  onChange={value => setFormData({ ...formData, expenses: value || 0 })}
                  min={0}
                  size="lg"
                  styles={{
                    label: { fontSize: rem(14), fontWeight: 600 },
                    input: { fontSize: rem(16), height: rem(45) }
                  }}
                />
              </Stack>
            </Card>

            {/* 備註 */}
            <Textarea
              label="備註"
              placeholder="輸入備註資訊..."
              value={formData.memo}
              onChange={event => setFormData({ ...formData, memo: event.target.value })}
              minRows={3}
              size="lg"
              styles={{
                label: { fontSize: rem(14), fontWeight: 600 },
                input: { fontSize: rem(16) }
              }}
            />
          </Stack>
        </Paper>

        {/* 總計與操作按鈕 */}
        <Paper shadow="md" radius="lg" p={{ base: 'md', sm: 'xl' }} bg="gray.0">
          <Stack gap="md">
            <Title order={3} size="md" c="dark.7">
              總計
            </Title>

            {/* 手機版：垃直堆疊 */}
            <Stack hiddenFrom="sm" gap="md" align="center">
              <Group justify="space-around" w="100%">
                <Stack gap="xs" align="center">
                  <Text size="sm" c="dimmed" fw={500}>
                    總收入
                  </Text>
                  <Text size="md" fw={700} c="green.6">
                    ${format_with_substring((formData.income || 0) + (formData.specialIncome || 0))}
                  </Text>
                </Stack>

                <Stack gap="xs" align="center">
                  <Text size="sm" c="dimmed" fw={500}>
                    總支出
                  </Text>
                  <Text size="md" fw={700} c="red.6">
                    ${format_with_substring(formData.expenses || 0)}
                  </Text>
                </Stack>
              </Group>

              <Stack gap="xs" align="center" w="100%">
                <Text size="sm" c="dimmed" fw={500}>
                  淨結餘
                </Text>
                <Text
                  size="xl"
                  fw={700}
                  c={netAmount >= 0 ? 'green.6' : 'red.6'}
                  p="xs"
                  style={{
                    border: `2px solid ${netAmount >= 0 ? '#51cf66' : '#fa5252'}`,
                    borderRadius: rem(8),
                    backgroundColor: netAmount >= 0 ? '#f0f9f0' : '#fff0f0'
                  }}
                >
                  ${format_with_substring(netAmount)}
                </Text>
              </Stack>
            </Stack>

            {/* 桌面版：水平排列 */}
            <Group visibleFrom="sm" justify="space-between" wrap="nowrap">
              <Stack gap="xs" align="flex-start">
                <Text size="sm" c="dimmed">
                  總收入
                </Text>
                <Text size="lg" fw={700} c="green.6">
                  ${format_with_substring((formData.income || 0) + (formData.specialIncome || 0))}
                </Text>
              </Stack>

              <Stack gap="xs" align="center">
                <Text size="sm" c="dimmed">
                  總支出
                </Text>
                <Text size="lg" fw={700} c="red.6">
                  ${format_with_substring(formData.expenses || 0)}
                </Text>
              </Stack>

              <Stack gap="xs" align="flex-end">
                <Text size="sm" c="dimmed">
                  淨結餘
                </Text>
                <Text size="lg" fw={700} c={netAmount >= 0 ? 'green.6' : 'red.6'}>
                  ${format_with_substring(netAmount)}
                </Text>
              </Stack>
            </Group>

            {/* 手機版：垅直排列按鈕 */}
            <Stack hiddenFrom="sm" gap="sm" mt="lg">
              <Button
                color="green"
                size="lg"
                leftSection={<IconCheck size={20} />}
                onClick={handleSave}
                loading={isSubmitting}
                fullWidth
                style={{
                  fontSize: rem(16),
                  height: rem(48)
                }}
              >
                儲存 (<Kbd size="xs">Ctrl+S</Kbd>)
              </Button>
              <Button
                variant="outline"
                color="gray"
                size="lg"
                leftSection={<IconRefresh size={20} />}
                onClick={handleReset}
                disabled={isSubmitting}
                fullWidth
                style={{
                  fontSize: rem(16),
                  height: rem(48)
                }}
              >
                重置 (<Kbd size="xs">Ctrl+R</Kbd>)
              </Button>
            </Stack>

            {/* 桌面版：水平排列按鈕 */}
            <Group visibleFrom="sm" justify="center" mt="lg" gap="md">
              <Button
                color="green"
                size="lg"
                leftSection={<IconCheck size={20} />}
                onClick={handleSave}
                loading={isSubmitting}
                style={{
                  fontSize: rem(16),
                  height: rem(50),
                  minWidth: rem(120)
                }}
              >
                儲存 (<Kbd size="xs">Ctrl+S</Kbd>)
              </Button>
              <Button
                variant="outline"
                color="gray"
                size="lg"
                leftSection={<IconRefresh size={20} />}
                onClick={handleReset}
                disabled={isSubmitting}
                style={{
                  fontSize: rem(16),
                  height: rem(50),
                  minWidth: rem(120)
                }}
              >
                重置 (<Kbd size="xs">Ctrl+R</Kbd>)
              </Button>
            </Group>

            {/* 快捷鍵提示 */}
            <Text size="xs" c="dimmed" ta="center" mt="sm">
              快速操作：<Kbd size="xs">Ctrl+T</Kbd> 今天 • <Kbd size="xs">Ctrl+S</Kbd> 儲存 •{' '}
              <Kbd size="xs">Ctrl+R</Kbd> 重置
            </Text>
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
};

export default DailyEdit;
